import _ from 'lodash';
import moment from 'moment';
import TransactionEntity from '../entities/TransactionsEntity';
import TagEntity from '../entities/TagEntity';
import NumberUtils from '../utils/NumberUtils';
import UserManagementStore from '../stores/UserManagementStore';
import {
  EXPORT_END_DATE_BUMP,
  TRANSACTION_REFUND_TYPE_ID,
  TRANSACTION_REFUND_SUB_TYPE_ID,
  TRANSACTION_CARD_TYPE_ID,
  TRANSACTION_ATM_TYPE_ID,
  TRANSACTION_IS_CARD_HOLDER_PRESENT,
} from '../constants/StringConstants';
import { MERCHANT_FOUND } from '../constants/EnrichedTransactionConstants';

// As per https://abouthere.atlassian.net/wiki/spaces/DYB/pages/1817282436/Transaction+Search+Dates+for+Future+Dated+Transactions
// If the endDate is today's date, then we bump the endDate for the API call to be +14 days,
export const bumpEndDateCheck = (endDateISO) => {
  if (moment().isSame(endDateISO, 'day')) {
    return moment(endDateISO).add(EXPORT_END_DATE_BUMP, 'days').toISOString();
  }
  return endDateISO;
};
class TransactionsParser {
  getTransactionsRequestPacket(
    accID,
    sortField,
    sortOrder,
    fromTransactionIndex,
    startDate,
    endDate
  ) {
    const requestData = {
      template: {
        file: 'matchAll',
      },
      params: {
        from: fromTransactionIndex,
        order: sortOrder,
        start_date: startDate,
        sort_field: sortField,
        end_date: bumpEndDateCheck(endDate),
        size: 25,
        account_list: Array.isArray(accID) ? accID : [accID],
      },
    };
    return requestData;
  }

  getFilteredTransactionsRequestPacket(
    accID,
    sortField,
    sortOrder,
    fromTransactionIndex,
    typeFilter,
    startDate,
    endDate
  ) {
    const requestData = {
      template: {
        file: 'matchAllWithFilter',
      },
      params: {
        from: fromTransactionIndex,
        order: sortOrder,
        start_date: startDate,
        sort_field: sortField,
        filter_field: '',
        filter_value: [],
        end_date: bumpEndDateCheck(endDate),
        size: 25,
        account_list: Array.isArray(accID) ? accID : [accID],
      },
    };
    switch (typeFilter) {
      case 'moneyin':
        requestData.params.filter_field = 'details.debit_credit';
        requestData.params.filter_value.push('credit');
        break;
      case 'moneyout':
        requestData.params.filter_field = 'details.debit_credit';
        requestData.params.filter_value.push('debit');
        break;
      case 'repeating':
        requestData.params.filter_field = 'details.type';
        requestData.params.filter_value.push('Direct Debit');
        requestData.params.filter_value.push('Standing Order');
        break;
    }
    return requestData;
  }

  getAssignTagRequestPacket(transactions, tag) {
    const requestData = {
      transactions: [],
      remember: false,
    };
    _.map(transactions, (transaction) => {
      const tagTransaction = {
        id: '',
        tag_ids: [],
        category_ids: [],
      };
      if (tag.tagBudget) {
        tagTransaction.tag_ids.push(tag.key);
      } else if (tag.categoryBudget) {
        tagTransaction.category_ids.push(parseInt(tag.key));
      }
      tagTransaction.id = transaction;
      requestData.transactions.push(tagTransaction);
    });
    return requestData;
  }

  getTransactionSuggestionRequestPacket(searchText, accountId) {
    const requestData = {
      template: {
        file: 'suggest',
      },
      params: {
        text: searchText,
        account_list: Array.isArray(accountId) ? accountId : [accountId],
      },
    };
    return requestData;
  }

  getSearchTransactionsRequestPacket(
    accountId,
    sortField,
    sortOrder,
    fromTransactionIndex,
    suggestData,
    startDate,
    endDate
  ) {
    const isNumeric = NumberUtils.isNumeric(suggestData);

    const requestData = {
      template: {
        file: 'matchText',
      },
      params: {
        from: fromTransactionIndex,
        ...(!isNumeric && { search_text: suggestData }),
        ...(isNumeric &&
          UserManagementStore.isSMEUser() && {
            transaction_amount: suggestData,
          }),
        order: sortOrder,
        start_date: startDate,
        sort_field: sortField,
        size: 25,
        end_date: bumpEndDateCheck(endDate),
        account_list: Array.isArray(accountId) ? accountId : [accountId],
      },
    };
    return requestData;
  }

  getFilteredSearchTransactionsRequestPacket(
    accountId,
    sortField,
    sortOrder,
    typeFilter,
    fromTransactionIndex,
    suggestData,
    startDate,
    endDate
  ) {
    const requestData = {
      template: {
        file: 'matchTextWithFilter',
      },
      params: {
        from: fromTransactionIndex,
        search_text: suggestData,
        order: sortOrder,
        start_date: startDate,
        sort_field: sortField,
        filter_field: '',
        filter_value: [],
        end_date: bumpEndDateCheck(endDate),
        size: 25,
        account_list: [accountId],
      },
    };
    switch (typeFilter) {
      case 'moneyin':
        requestData.params.filter_field = 'details.debit_credit';
        requestData.params.filter_value.push('credit');
        break;
      case 'moneyout':
        requestData.params.filter_field = 'details.debit_credit';
        requestData.params.filter_value.push('debit');
        break;
      case 'repeating':
        requestData.params.filter_field = 'details.type';
        requestData.params.filter_value.push('Direct Debit');
        requestData.params.filter_value.push('Standing Order');
        break;
    }
    return requestData;
  }

  /**
   * Horrible, swiss cheese regex based on the current
   * iOS and Android implementation from 2016 (approved by SD's and PO's).
   * In a perfect world we'd get the proper `merchantName` from our db.
   */
  getMerchantNameFromNarrative(narrative) {
    const narrativeParts = narrative.split(', ');
    const [firstPart, secondPart, thirdPart] = narrativeParts;

    const cardPaymentRegex = /^([a-zA-Z]+\s\d{0,2})$/;
    const noSpecialCharactersRegex = /^([a-zA-Z0-9]*)$/;

    const getMatch = () => {
      if (narrativeParts.length === 2) {
        if (firstPart.match(cardPaymentRegex)) {
          return secondPart;
        }

        if (secondPart.match(cardPaymentRegex)) {
          return firstPart;
        }

        if (firstPart.match(noSpecialCharactersRegex)) {
          return secondPart;
        }

        if (secondPart.match(noSpecialCharactersRegex)) {
          return firstPart;
        }
      }

      // Feels off..
      if (
        narrativeParts.length === 3 &&
        (firstPart.match(cardPaymentRegex) ||
          firstPart.match(noSpecialCharactersRegex))
      ) {
        return `${secondPart}, ${thirdPart}`;
      }
    };

    return getMatch() ?? narrative;
  }

  getEnrichedDataRequestPacket(fetchedTransactions) {
    return fetchedTransactions.reduce(
      (requestData, transaction) => {
        // We only want to enrich the Card and ATM transactions (GDPR reasons)
        const isNotAllowedToBeEnriched =
          !transaction.isCardType && !transaction.isATMType;
        if (isNotAllowedToBeEnriched) {
          return requestData;
        }

        requestData.searchCriteria.push({
          merchantCriteria: {
            cardAcceptorName: transaction.merchantName,
            cardAcceptorLocation: transaction.location.city,
            cardAcceptorRegionCode: transaction.location.regionCode,
            cardAcceptorCountryCode: transaction.location.countryCode,
            merchantCategoryCode: transaction.merchantCategoryCode,
          },
        });

        requestData.transactionIdentifiers.push(transaction.id);

        return requestData;
      },
      { searchCriteria: [], transactionIdentifiers: [] }
    );
  }

  parseTransactionsResponse(response) {
    const transactions = [];
    _.map(response.hits.hits, (transaction) => {
      const transactionEntity = new TransactionEntity();
      transactionEntity.id = transaction._id;
      transactionEntity.date = moment(transaction._source.details.when).format(
        'DD MMM YY'
      );

      transactionEntity.dateRaw = transaction._source.details.when;
      transactionEntity.description =
        transaction._source.details.narrative.large;

      if (transaction._source.metadata.where.city) {
        transactionEntity.description = `${transactionEntity.description} ${transaction._source.metadata.where.city}`;
      }

      const typeId = transaction._source.details.type_id;
      if (typeId === TRANSACTION_CARD_TYPE_ID) {
        transactionEntity.isCardType = true;
      }

      if (typeId === TRANSACTION_ATM_TYPE_ID) {
        transactionEntity.isATMType = true;
      }

      transactionEntity.isRefund =
        typeId === TRANSACTION_REFUND_TYPE_ID &&
        transaction._source.details.sub_type_id ===
          TRANSACTION_REFUND_SUB_TYPE_ID;

      /**
       * As a part of the agreed spec
       * force the "type" string for refund transactions.
       */
      transactionEntity.type =
        transactionEntity.isRefund && !transaction._source.details.posted
          ? 'Card Refund'
          : transaction._source.details.type;

      if (transaction._source.metadata.categories) {
        transactionEntity.tag =
          transaction._source.metadata.categories[0].value;
        transactionEntity.tagId = transaction._source.metadata.categories[0].id;
        transactionEntity.archived =
          transaction._source.metadata.categories[0].archived;
      }
      if (transaction._source.metadata.tags) {
        transactionEntity.tag = transaction._source.metadata.tags[0].value;
        transactionEntity.tagId = transaction._source.metadata.tags[0].id;
        transactionEntity.archived =
          transaction._source.metadata.tags[0].archived;
      }
      if (
        transaction._source.metadata.tags === null &&
        transaction._source.metadata.categories === null
      ) {
        transactionEntity.tag = 'Untagged';
      }

      transactionEntity.balance = NumberUtils.appendCurrency(
        '{}',
        transaction._source.details.balances.current.value,
        transaction._source.details.balances.current.currency
      );

      transactionEntity.amount = NumberUtils.appendCurrency(
        '{}',
        transaction._source.details.amount.value,
        transaction._source.details.amount.currency
      );
      transactionEntity.rawAmount = transaction._source.details.amount.value;

      const absAmount = NumberUtils.appendCurrency(
        '{}',
        Math.abs(transaction._source.details.amount.value),
        transaction._source.details.amount.currency
      );

      if (transaction._source.details.amount.value > 0) {
        transactionEntity.credit = absAmount;
        transactionEntity.debit = '';
      } else {
        transactionEntity.debit = absAmount;
        transactionEntity.credit = '';
      }
      transactionEntity.account = transaction._source.this_account.number;
      transactionEntity.sortCode = transaction._source.this_account.sort_code;
      transactionEntity.accountId = transaction._source.this_account.uuid;
      transaction._source.details.posted
        ? (transactionEntity.posted = true)
        : (transactionEntity.posted = false);

      transactionEntity.merchantName = this.getMerchantNameFromNarrative(
        transaction._source.details.narrative.small
      );

      if (transaction._source.metadata.where) {
        transactionEntity.location.city =
          transaction._source.metadata.where.city || null;
        transactionEntity.location.countryCode =
          transaction._source.metadata.where.country?.code || null;
        transactionEntity.location.regionCode =
          transaction._source.metadata.where.county || null;
      }

      if (transaction._source.details.merchant_category_code?.length < 5) {
        transactionEntity.merchantCategoryCode =
          transaction._source.details.merchant_category_code || null;
      }

      transactionEntity.isCardHolderPresent =
        transaction._source.details.cardholderpresent ===
        TRANSACTION_IS_CARD_HOLDER_PRESENT;

      transactionEntity.reference = transaction._source.details.reference;

      transactions.push(transactionEntity);
    });
    return transactions;
  }

  parseTagsResponse(response) {
    const tags = [];
    if (response.tags.length > 0) {
      _.map(response.tags, (tag) => {
        const tagEntity = new TagEntity();
        tagEntity.key = tag.id.toString();
        tagEntity.value = tag.value;
        tagEntity.tagBudget = true;
        tagEntity.categoryBudget = false;
        tags.push(tagEntity);
      });
    }
    if (response.categories.length > 0) {
      _.map(response.categories, (tag) => {
        const tagEntity = new TagEntity();
        tagEntity.key = tag.id.toString();
        tagEntity.value = tag.value;
        tagEntity.isArchived = tag.archived || false;
        tagEntity.path = tag.path;
        tagEntity.tagBudget = false;
        tagEntity.categoryBudget = true;
        tags.push(tagEntity);
      });
    }
    return tags;
  }

  parseSuggestResponse(response) {
    const suggestData = [];
    _.map(response.suggest[0].options, (suggest) => {
      const data = {
        text: suggest.text,
      };
      suggestData.push(data);
    });
    return suggestData;
  }

  parseEnrichedDataResponse(response, transactionIdentifiers) {
    if (!response) {
      return;
    }

    return response.reduce((data, { merchantResult }, index) => {
      // Edge case
      if (!merchantResult) {
        return data;
      }

      const {
        resultStatus,
        merchantName: name,
        logos,
        merchantLocation: geolocation,
        address,
      } = merchantResult;

      /**
       * We can simply ignore the top level `resultStatus`,
       * as it errors only when the POST `searchCriteria` is empty.
       *
       * Current implementation prevents additional /transactions/enrich
       * call in that scenario.
       *
       * We do care about the inner `resultStatus` though - we want
       * to feed our `Map` when merchant is found.
       */
      if (resultStatus.code !== MERCHANT_FOUND) {
        return data;
      }

      data.set(transactionIdentifiers[index], {
        name,
        logos,
        geolocation,
        address,
      });

      return data;
    }, new Map());
  }

  getAddNewTagPacket(newTagValue) {
    const requestData = {
      value: newTagValue,
    };
    return requestData;
  }

  getEditExistingTagPacket(id, editTagValue) {
    const requestData = {
      id,
      value: editTagValue,
    };
    return requestData;
  }
}

export default new TransactionsParser();
